import { createAsyncThunk, createSelector, createEntityAdapter, createSlice, EntityState } from '@reduxjs/toolkit';
import { AppState } from '.';
import { AccountFeatureFlag } from '../models';
import { Operator, Query } from '../models/query';
import { AccountFeatureFlagService } from '../services';

export const ACCOUNT_FEATURE_FLAG_KEY = 'accountFeatureFlags';

interface AccountFeatureFlagState extends EntityState<AccountFeatureFlag> {
  loading: boolean;
}

const AccountFeatureFlagAdapter = createEntityAdapter<AccountFeatureFlag>();

export const createInitialState = (): AccountFeatureFlagState => (
  AccountFeatureFlagAdapter.getInitialState({
    loading: false,
  })
);

export const doGetByAccountId = createAsyncThunk(
  'AccountFeatureFlags/GgtByOrganizationId',
  async (input: Required<Pick<AccountFeatureFlag, 'accountId'>>) => {
    const criteria: Query<AccountFeatureFlag>[] = [
      {
        field: 'accountId',
        operator: Operator.EQ,
        value: input.accountId,
      },
      {
        field: 'archived',
        operator: Operator.EQ,
        value: false,
      },
      { limit: 1 },
    ];

    // unique organizationId of AccountFeatureFlag => max 1 record
    const data = await AccountFeatureFlagService.getAccountFeatureFlags(...criteria);
    return data;
  },
);

const accountFeatureFlagSlice = createSlice({
  name: ACCOUNT_FEATURE_FLAG_KEY,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(doGetByAccountId.pending, state => {
      state.loading = true;
    });
    builder.addCase(doGetByAccountId.fulfilled, (state, action) => {
      state.loading = false;
      AccountFeatureFlagAdapter.setAll(state, action.payload.data);
    });
    builder.addCase(doGetByAccountId.rejected, state => {
      state.loading = false;
    });
  },
});

const selectAccountFeatureFlagFeature = (
  state: AppState,
) => state[ACCOUNT_FEATURE_FLAG_KEY];

export const {
  selectAll: selectAllAccountFeatureFlags,
  selectIds,
  selectEntities,
} = AccountFeatureFlagAdapter.getSelectors(selectAccountFeatureFlagFeature);

export const selectByAccountId = (accountId: AccountFeatureFlag['accountId']) => createSelector(
  selectAllAccountFeatureFlags,
  all => all.find(it => it.accountId === accountId),
);

export const selectIsAccountSsoEnabled = (accountId: AccountFeatureFlag['accountId']) => createSelector(
  selectByAccountId(accountId),
  featureFlag => featureFlag && featureFlag.sso,
);

export const selectIsAccountWhitelistEnabled = (accountId: AccountFeatureFlag['accountId']) => createSelector(
  selectByAccountId(accountId),
  featureFlag => featureFlag && featureFlag.whitelistIp,
);

export default accountFeatureFlagSlice.reducer;
